import { useQuery } from '@tanstack/react-query';
import { runObsFrom } from './client';
import type { LatestRunForStatus } from './ai-auto-status';

const SELECT_COLUMNS = 'status, started_at, finished_at, error_summary';

/**
 * SHAPE A, narrowed to one row — the freshest `workflow_run` for a conversation. RLS scopes to the
 * caller's tenant (see wr_tenant_read, supabase/migrations/20260603190000_obs_spine_tables.sql), so
 * this is safe to query without an explicit tenant filter, same as useWorkflowRuns.
 *
 * Polls only while the freshest run is 'running' — same "no polling once terminal" posture as
 * useWorkflowRuns/useRunSteps (completed runs are immutable), except here a live run is exactly
 * the case the AI Auto Mode badge needs to catch transitioning to failed/succeeded promptly.
 */
export function useLatestConversationRun(conversationId: string | null | undefined) {
  return useQuery({
    queryKey: ['latest-conversation-run', conversationId],
    enabled: !!conversationId,
    staleTime: 0,
    queryFn: async (): Promise<LatestRunForStatus | null> => {
      const { data, error } = await runObsFrom('workflow_run')
        .select(SELECT_COLUMNS)
        .eq('conversation_id', conversationId)
        .order('started_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('[run-observability] useLatestConversationRun failed:', error);
        throw error;
      }
      const rows = (data ?? []) as LatestRunForStatus[];
      return rows[0] ?? null;
    },
    refetchInterval: (query) => (query.state.data?.status === 'running' ? 4_000 : false),
  });
}
